import React from 'react';
import {
  Button,
  Drawer,
  Tabs,
  Modal,
  Radio,
  Tooltip,
  Spin,
  message
} from 'antd';
import style from './index.scss';
import {
  findIncludeCurrentWithNonZeroEnd,
  includeCurrentWithNonZeroEndMessage
} from './includeCurrentTransactionValidation';
// FIN-12351: the two groupingKeyToValueMapping rules the BE enforces, mirrored so a variant
// cannot be saved into a rejection.
import {
  findMappingViolations,
  mappingViolationMessage
} from './groupingKeyMappingValidation';
import BasicInfo from './Tabs/BasicInfo';
import Formula from './Tabs/Formula';
import Functions from './Tabs/Functions';
import { shouldResyncRecord } from './Tabs/functionInfoUpdates';
import FilterConditions from './Tabs/FilterConditions';
import ScoringOverrides from './Tabs/ScoringOverrides';
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { triggeredOnOptions } from './config';
import { isScoringOverridesDisabled } from './scoringOverridesLock';
import ErrorBoundary from '../../../../components/ErrorBoundary';

// FIN-11822: scoped fallback for a single tab's render crash. Keeps the
// rest of the drawer interactive (other tabs, header, Save/Close) when
// one tab's render path throws.
const TabErrorFallback = ({ tabName }) => (
  <div style={{ padding: 24, color: '#cf1322' }}>
    <ExclamationCircleOutlined style={{ marginRight: 8 }} />
    Something went wrong rendering the {tabName} tab. Try switching to another
    tab and back, or reload the page. The drawer remains open so you can switch
    tabs.
  </div>
);

const { TabPane } = Tabs;
const { confirm } = Modal;

// Adapter that bridges the presentational ScoringOverrides component (params/onChange)
// to the drawer's variant-render-data update pattern used by sibling tabs.
const ScoringOverridesTab = ({
  variantInfo,
  setVariantRenderData,
  typoIndex,
  variantIndex,
  isReadOnly,
  generationState,
  onDirtyField
}) => {
  const params = variantInfo?.scoringParameters ?? null;
  const matchAction = variantInfo?.matchAction ?? null;
  const handleChange = (next) => {
    setVariantRenderData((prev) => {
      const dataToSet = prev;
      dataToSet[typoIndex].variants[variantIndex].scoringParameters = next;
      return [...dataToSet];
    });
    if (onDirtyField) onDirtyField();
  };
  return (
    <ScoringOverrides
      params={params}
      onChange={handleChange}
      disabled={isScoringOverridesDisabled(isReadOnly, generationState)}
      matchAction={matchAction}
    />
  );
};

const tabsConfig = [
  {
    componentName: <BasicInfo />,
    tabName: 'Basic Info'
  },
  {
    componentName: <FilterConditions />,
    tabName: 'Global Conditions'
  },
  {
    componentName: <Functions />,
    tabName: 'Functions'
  },
  {
    componentName: <Formula />,
    tabName: 'Formula'
  },
  {
    componentName: <ScoringOverridesTab />,
    tabName: 'Scoring Overrides'
  }
];

export default class ConfigureDrawer extends React.Component {
  state = {
    visible: false,
    activeTabID: 0,
    record: {},
    isDirty: false,
    disableAction: false,
    showIncompleteError: false,
    incompleteFields: []
  };

  showDrawer = (defaultTab, record, typoIndex, variantIndex) => {
    this.setState({
      visible: true,
      record: {
        ...record,
        variantIndex,
        typoIndex
      },
      isDirty: false,
      disableAction: false,
      showIncompleteError: false,
      incompleteFields: []
    });
  };

  onDirtyField = () => {
    this.setState({
      isDirty: true
    });
  };

  onClose = () => {
    this.setState({
      visible: false,
      disableAction: false,
      showIncompleteError: false,
      incompleteFields: []
    });
  };

  onDisableActions = (isDisable, isIncompleteRule, incompleteFields = []) => {
    this.setState({
      disableAction: isDisable,
      showIncompleteError: isDisable && isIncompleteRule,
      // FIN-13260 — carried through so the footer can say which leaf is at fault.
      incompleteFields: isDisable ? incompleteFields : []
    });
  };

  tabsCallback = (tabID) => {
    this.setState({ activeTabID: parseInt(tabID) });
  };

  static getDerivedStateFromProps(props, state) {
    // FIN-13389: `state.record.variantIndex > -1` was right by accident. It ignored typoIndex,
    // and `null > -1` coerces to `0 > -1` and PASSES — a record carrying null indices then read
    // variantRenderData[null].variants[null] and threw here, unmounting the drawer.
    if (shouldResyncRecord(state.record)) {
      const currentRecord =
        props.variantRenderData?.[state.record.typoIndex]?.variants?.[
          state.record.variantIndex
        ];
      if (currentRecord && currentRecord !== state.record) {
        return {
          record: {
            ...currentRecord,
            variantIndex: state.record.variantIndex,
            typoIndex: state.record.typoIndex
          }
        };
      }
    }
    return null;
  }

  showCancelConfirm = () => {
    if (
      (this.props.isReadOnly && this.props.generationState !== 'generated') ||
      !this.state.isDirty
    ) {
      this.onClose();
      return;
    }
    confirm({
      title: 'Do you want to cancel changes?',
      icon: <ExclamationCircleOutlined />,
      content: 'Changes done in rules will be lost.',
      onOk: () => {
        this.props.setScenarioData();
        this.onClose();
      }
    });
  };

  showSaveConfirm = (typologyId) => {
    // Block save on a contradictory aggregation config: "Include Current" with a non-zero lookback
    // "to" is a silent no-op (the current txn is outside the window and always excluded). Make the
    // author resolve it (set end = 0, or uncheck Include Current) rather than save a misleading rule.
    const includeCurrentOffenders = findIncludeCurrentWithNonZeroEnd(
      this.state.record
    );
    if (includeCurrentOffenders.length > 0) {
      message.error(
        includeCurrentWithNonZeroEndMessage(includeCurrentOffenders),
        6
      );
      return;
    }

    // FIN-12351: a redirect whose grouping key was deselected, or one combined with "Include
    // Current", is rejected at rule load with a cursor-path error that names neither the function
    // nor the fix. Name both here instead.
    const mappingViolations = findMappingViolations(this.state.record);
    if (mappingViolations.length > 0) {
      message.error(mappingViolationMessage(mappingViolations), 6);
      return;
    }

    // FIN-11794: pass the currently-edited variant's ruleId so the BE narrows
    // validateRules to just that variant. The drawer is variant-scoped; the
    // user can correct broken sibling variants one at a time without sibling
    // failures blocking this save.
    const activeVariantId = this.state.record && this.state.record.ruleId;
    confirm({
      title: 'Save Changes!!',
      icon: <ExclamationCircleOutlined />,
      content:
        'Please check if the variant configurations are completed (Ex: Group_By) and set as per the variant.',
      onOk: () => {
        this.props.handleSaveTypology(typologyId, activeVariantId);
      }
    });
  };

  onChangeTriggeredOn = (e) => {
    const { variantIndex, typoIndex } = this.state.record;

    this.props.setVariantRenderData((prev) => {
      let dataToSet = prev;
      dataToSet[typoIndex].variants[variantIndex].inputType = e.target.value;
      return [...dataToSet];
    });
    this.onDirtyField();
  };

  render() {
    const {
      typologies = [],
      setVariantRenderData,
      isReadOnly,
      tooltips,
      generationState,
      thresholdResults
    } = this.props;
    const {
      record = {},
      isDirty,
      disableAction,
      showIncompleteError,
      incompleteFields
    } = this.state;
    const {
      variantName = '',
      typologyId = '',
      variantIndex,
      typoIndex,
      typologiesReadOnly = [],
      inputType: triggeredOn = 'transaction'
    } = record;
    let selectedTypology = {};
    if (typologies.length > 0) {
      selectedTypology = typologies.find((t) => t.id === typologyId);
    } else {
      selectedTypology = typologiesReadOnly.find((t) => t.id === typologyId);
    }
    return (
      <>
        <Drawer
          title={variantName}
          width={720}
          onClose={this.showCancelConfirm}
          visible={this.state.visible}
          bodyStyle={{ paddingBottom: 80 }}
          destroyOnClose={true}
          maskClosable={false}
          footer={
            isReadOnly && generationState !== 'generated' ? (
              <></>
            ) : (
              <div
                className={style.footerContainer}
                style={{
                  justifyContent: showIncompleteError
                    ? 'space-between'
                    : 'flex-end'
                }}
              >
                {showIncompleteError && (
                  <div className={style.incompleteError}>
                    {incompleteFields && incompleteFields.length
                      ? `Incomplete rule on: ${incompleteFields.join(
                          ', '
                        )}. Complete it to save.`
                      : 'Incomplete rules found, please complete it.'}
                  </div>
                )}
                <div
                  style={{
                    textAlign: 'right'
                  }}
                >
                  {/* <Button onClick={this.onClose} style={{ marginRight: 8 }}>
                  Cancel
                </Button> */}
                  <Button
                    onClick={() => {
                      this.showSaveConfirm(typologyId);
                    }}
                    disabled={!isDirty || disableAction}
                    type="primary"
                  >
                    Save
                  </Button>
                </div>
              </div>
            )
          }
        >
          {/* FIN-12017 — coerce to a real boolean. Read-only consumers
              (ScoreExplainability/preventionTable, TypologyRulesConfig) render
              <ConfigureDrawer> with no isLoading prop, so it is undefined — and
              antd's Spin treats `spinning === undefined` as its default `true`,
              spinning forever over already-loaded content. !! maps undefined to
              false; a real boolean from the configure page is unchanged. */}
          <Spin spinning={!!this.props.isLoading}>
            <div className={style.typologyTriggerdContainer}>
              <label>Variant Triggered On</label>
              <Radio.Group
                className={
                  !isReadOnly &&
                  record?.variantName?.startsWith('##_TT_MM_QA_##')
                    ? ''
                    : 'radio-group-disabled'
                }
                disabled={isReadOnly}
                options={triggeredOnOptions}
                value={triggeredOn}
                optionType="button"
                buttonStyle="solid"
                size="small"
                onChange={(e) => {
                  this.onChangeTriggeredOn(e);
                }}
              />
              <Tooltip
                title={tooltips?.typology_triggered_by}
                placement="bottom"
              >
                <InfoCircleOutlined size={16} twoToneColor="#000" />
              </Tooltip>
            </div>
            <Tabs onChange={this.tabsCallback} destroyInactiveTabPane={true}>
              {tabsConfig.map((tabConfig, idX) => {
                const { componentName, tabName } = tabConfig;
                return (
                  <TabPane
                    tab={
                      <span
                        className={
                          this.state.activeTabID === idX
                            ? style.tabClassActive
                            : style.tabClass
                        }
                      >
                        {tabName}
                      </span>
                    }
                    /* FIN-13260 — rule validity must NEVER gate tab NAVIGATION. An incomplete
                       leaf (a stored is_in_lookup_list with valueSrc:["field"], which RAQB
                       resolves to value=[null]) raised disableAction and locked every sub-tab,
                       so the analyst could not reach the tab holding the broken rule to fix it.
                       Save is still gated by disableAction below — that is the correct gate.
                       Merge note: the base still passed disabled={disableAction} here; that is
                       the attribute this ticket removes, so the base side is deliberately
                       dropped rather than merged. */
                    key={idX}
                  >
                    {/* FIN-11822: scope render-throws to the tab — keeps
                        the drawer + other tabs intact instead of letting
                        React unmount the whole drawer subtree. */}
                    <ErrorBoundary
                      fallback={<TabErrorFallback tabName={tabName} />}
                    >
                      {React.cloneElement(componentName, {
                        typology: selectedTypology,
                        setVariantRenderData,
                        variantIndex,
                        typoIndex,
                        variantInfo: record,
                        isReadOnly,
                        onDirtyField: this.onDirtyField,
                        disableActions: this.onDisableActions,
                        generationState,
                        thresholdResults
                      })}
                    </ErrorBoundary>
                  </TabPane>
                );
              })}
            </Tabs>
          </Spin>
        </Drawer>
      </>
    );
  }
}
